import jwt from 'jsonwebtoken';
import {useEffect, useState} from 'react';
import PingAuthContextProps from './types/PingAuthContextProps';
import {emptyAuthInfo, PingAuthInfo} from './types/PingAuthInfo';
import PingAuthProperties from './types/PingAuthProperties';
import {PingAuthStatus} from './types/PingAuthStatus';
import {emptyPingUser, getPingUserDetails, PingUser} from './types/PingUser';
import {injectTokenAxiosRequests} from './injectTokenAxiosRequests';
import {
    gatherAuthInfoFromUrl,
    isAccessTokenValid,
    readAuthInfo,
    refreshAccessTokenCookies,
    removeAllCookies,
    setAuthInfoCookie,
} from './PingAuthUtil';
import {APP_COMMIT, APP_DATESTAMP, APP_VERSION} from '~/utils/Constants';

let cachedAccessToken: string | undefined;

export const usePingAuth = ({
    loginUrl,
    applicationId,
    callbackDomain,
}: PingAuthProperties): PingAuthContextProps => {
    const [authInfo, setAuthInfo] = useState<PingAuthInfo>(emptyAuthInfo);
    const [user, setUser] = useState<PingUser>(emptyPingUser);
    const [status, setStatus] = useState<PingAuthStatus>(PingAuthStatus.NOT_INITIALIZED);

    const initDebugGlobals = (authInfo: PingAuthInfo): void => {
        const versionDetails = {APP_VERSION, APP_COMMIT, APP_DATESTAMP};
        globalThis.debug = {
            ...globalThis.debug,
            versionDetails,
        };
        if (authInfo?.tokens?.accessToken) {
            const tokenData = jwt.decode(authInfo.tokens.accessToken);
            globalThis.debug = {
                ...globalThis.debug,
                tokenData,
                authInfo,
            };
        }
    };

    const initWith = async (newAuthInfo: PingAuthInfo): Promise<void> => {
        const newUser: PingUser = getPingUserDetails(newAuthInfo.tokens);
        setUser(newUser);
        setAuthInfo(newAuthInfo);
        cachedAccessToken = newAuthInfo.tokens.accessToken;
        injectTokenAxiosRequests();
        initDebugGlobals(newAuthInfo);
    };

    const initialize = async () => {
        const currentUrl = new URL(window.location.href);
        const isCallbackUrl = currentUrl.searchParams.has('tokens');

        if (isCallbackUrl) {
            const newAuthInfo: PingAuthInfo = gatherAuthInfoFromUrl();
            setAuthInfoCookie(newAuthInfo);
            await initWith(newAuthInfo);
            setStatus(PingAuthStatus.LOGGED_IN);
            // Replace the url in the browser, removing the parameters, without refreshing the page
            window.history.replaceState({}, document.title, currentUrl.href.split('?')[0]);
        } else {
            const storedAuthInfo = readAuthInfo();
            if (storedAuthInfo) {
                if (isAccessTokenValid(storedAuthInfo)) {
                    await initWith(storedAuthInfo);
                    setStatus(PingAuthStatus.LOGGED_IN);
                } else {
                    refreshAccessTokenCookies()
                        .then((freshAuthInfo) => {
                            initWith(freshAuthInfo);
                            setStatus(PingAuthStatus.LOGGED_IN);
                        })
                        .catch((error) => {
                            window.console.log({
                                _msg: 'Error while refreshing auth tokens!',
                                error,
                            });
                            setStatus(PingAuthStatus.ERROR);
                        });
                }
            } else {
                setStatus(PingAuthStatus.NOT_LOGGED_IN);
            }
        }
    };

    const logIn = () => {
        const callbackUrl = `${window.location.protocol}//${callbackDomain}`;
        window.location.href = `${loginUrl}?application=${applicationId}&callback=${callbackUrl}`;
    };

    const logOut = () => {
        setAuthInfo(emptyAuthInfo);
        setUser(emptyPingUser);
        removeAllCookies();
        cachedAccessToken = '';
        logIn();
    };

    const updateUser = () => {};

    useEffect(() => {
        if (status === PingAuthStatus.NOT_INITIALIZED) {
            setStatus(PingAuthStatus.INITIALIZING);
            initialize(); // Will further update the status with the results
        }
        if (status === PingAuthStatus.LOGGED_IN) {
            updateUser();
            injectTokenAxiosRequests();
        }
        if (status === PingAuthStatus.NOT_LOGGED_IN || status === PingAuthStatus.ERROR) {
            // Remove the injector so the tokens are no longer included
            injectTokenAxiosRequests(false);
        }
    }, [status]);

    return {tokens: authInfo.tokens, logIn, logOut, user, status};
};

/**
 * Convenience methods for class components that can't use React hooks.
 */
export const getPingAccessToken = (): string | undefined => cachedAccessToken;
